import { ActionTree } from 'vuex'

import api from '@/apiClient'

import { Invoice, InvoiceFilters } from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../../state'

import {
  ADD_INVOICE_ORDERS
} from '../orders/mutations'

import {
  ADD_INVOICE_ORDER_PAYMENTS
} from '../orderPayments/mutations'

import {
  ADD_INVOICE,
  APPLY_INVOICE_FILTERS,
  INCREMENT_INVOICES_QUERY_INDEX,
  LOADED_ALL_INVOICES,
  RESET_LOADED_ALL_INVOICES,
  RESET_INVOICES_QUERY_INDEX,
  SET_INVOICES_DEFAULT_FILTERS_FLAG,
  UPDATE_INVOICES,
  UPDATE_INVOICES_TOTAL
} from './mutations'

import { OrderingState } from '../state'

const actions: ActionTree<OrderingState, RootState> = {
  /**
   * Adds a new Invoice and prepares its relational objects.
   *
   * @param invoice Invoice to be added.
   */
  addInvoice ({ commit, state }, invoice: Invoice) {
    if (state.invoices.orders[invoice.id] === undefined) {
      commit(ADD_INVOICE_ORDERS, invoice.id)
    }

    commit(ADD_INVOICE, invoice)

    commit(UPDATE_INVOICES_TOTAL, state.invoices.total + 1)

    if (state.invoices.orderPayments[invoice.id] === undefined) {
      commit(ADD_INVOICE_ORDER_PAYMENTS, invoice.id)
    }
  },

  /**
   * Applies InvoiceFilters to the query and resets the loading state.
   *
   * @param filters InvoiceFilters to be applied.
   * @param isDefaultFilters Flags if `filters` is default filters.
   */
  changeInvoiceFilter (
    { commit },
    { filters, isDefaultFilters }: { filters: InvoiceFilters; isDefaultFilters: boolean }
  ) {
    commit(APPLY_INVOICE_FILTERS, filters)
    commit(SET_INVOICES_DEFAULT_FILTERS_FLAG, isDefaultFilters)

    commit(RESET_LOADED_ALL_INVOICES)
    commit(RESET_INVOICES_QUERY_INDEX)
  },

  /**
   * Loads one Invoice.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Invoice id to be loaded.
   */
  async loadInvoice ({ commit, dispatch, getters, rootState, rootGetters, state }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const invoice = await api.invoices.fetchInvoice(tokenV2, providerId, businessUnit.id, id)

    if (getters.invoiceById(id) === null) {
      await dispatch('addInvoice', invoice)
    } else {
      const invoices = mergeById(state.invoices.elements, [invoice])
      commit(UPDATE_INVOICES, invoices)
    }
  },

  /**
   * Loads a list of Invoices.
   *
   * It rejects if the user is not logged in.
   *
   * It prepares the relational objects for each loaded Invoice.
   *
   * @param invoiceIds Invoice id list to be loaded.
   */
  async loadInvoiceList (
    { commit, getters, rootState, rootGetters, state },
    invoiceIds: number[]
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const invoices = await Promise.all(
      [...new Set(invoiceIds)].map((id) => api.invoices.fetchInvoice(tokenV2, providerId, businessUnit.id, id))
    )

    let count = 0

    for (const invoice of invoices) {
      if (getters.invoiceById(invoice.id) === null) {
        count++
      }

      if (state.invoices.orders[invoice.id] === undefined) {
        commit(ADD_INVOICE_ORDERS, invoice.id)
      }

      if (state.invoices.orderPayments[invoice.id] === undefined) {
        commit(ADD_INVOICE_ORDER_PAYMENTS, invoice.id)
      }
    }

    commit(UPDATE_INVOICES, mergeById(state.invoices.elements, invoices))

    commit(UPDATE_INVOICES_TOTAL, state.invoices.total + count)
  },

  /**
   * Loads a page of Invoices.
   *
   * It rejects if the user is not logged in.
   *
   * It prepares the relational objects for each loaded Invoice.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   */
  async loadInvoicePage ({ commit, getters, rootState, rootGetters, state }) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.invoices.fetchInvoicePage(
      tokenV2,
      providerId,
      businessUnit.id,
      state.invoices.query
    )

    if (state.invoices.total !== page.total) {
      commit(UPDATE_INVOICES_TOTAL, page.total)
    }

    if (page.elements.length > 0) {
      for (const invoice of page.elements) {
        if (state.invoices.orders[invoice.id] === undefined) {
          commit(ADD_INVOICE_ORDERS, invoice.id)
        }

        if (state.invoices.orderPayments[invoice.id] === undefined) {
          commit(ADD_INVOICE_ORDER_PAYMENTS, invoice.id)
        }
      }

      const invoices = mergeById(state.invoices.elements, page.elements)

      commit(UPDATE_INVOICES, invoices)

      if (getters.filteredInvoices.length === page.total) {
        commit(LOADED_ALL_INVOICES)
      }

      commit(INCREMENT_INVOICES_QUERY_INDEX, page.elements.length)
    } else {
      commit(RESET_INVOICES_QUERY_INDEX)

      if (page.total === 0 && state.invoices.elements.length > 0) {
        commit(UPDATE_INVOICES, [])
      }
    }
  },

  /**
   *  Closes an Invoice.
   *
   * It rejects if the user is not logged in.
   *
   * @param Id Invoice id to be closed.
   */
  async closeInvoice ({ dispatch, rootGetters, rootState }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    await api.invoices.closeInvoice(tokenV2, providerId, businessUnit.id, id)

    if (!rootGetters.isWebSocketConnected) {
      await dispatch('loadInvoice', id)
    }
  }
}

export default actions
